import { Divider, Drawer, Group, SimpleGrid, Stack } from '@mantine/core';
import type { CeleryWorker } from '@repo/api-codegen';
import { Icon, Text, Title } from '@repo/foundations';

interface WorkerDetailDrawerProps {
	worker: CeleryWorker | null;
	onClose: () => void;
}

export function WorkerDetailDrawer({
	worker,
	onClose,
}: WorkerDetailDrawerProps) {
	if (!worker) return null;

	return (
		<Drawer
			opened={!!worker}
			onClose={onClose}
			title={
				<Group spacing="xs">
					<Icon name="serverBolt" color="icon/primary/default" size="md" />
					<Title size="md" weight="bold" truncate>
						Worker: {worker.name}
					</Title>
				</Group>
			}
			position="right"
			size="xl"
		>
			<Stack spacing="xs">
				{/* Basic Information */}
				<Stack spacing="xs">
					<Title size="md" weight="bold">
						Basic Info
					</Title>
					<SimpleGrid cols={2}>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Process ID
							</Title>
							<Text size="sm">{worker.pid}</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Uptime
							</Title>
							<Text size="sm">{Math.floor(worker.uptime / 60)} minutes</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Clock
							</Title>
							<Text size="sm">{worker.clock}</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Prefetch Count
							</Title>
							<Text size="sm">{worker.prefetch_count}</Text>
						</Stack>
					</SimpleGrid>
				</Stack>

				<Divider />

				{/* Pool Information */}
				<Stack spacing="xs">
					<Title size="md" weight="bold">
						Pool Info
					</Title>
					<SimpleGrid cols={2}>
						{Object.entries(worker.pool || {}).map(([key, value]) => (
							<Stack key={key} spacing={0}>
								<Title size="sm" weight="bold">
									{key}
								</Title>
								<Text size="sm" truncate>
									{typeof value === 'object'
										? JSON.stringify(value)
										: String(value)}
								</Text>
							</Stack>
						))}
					</SimpleGrid>
				</Stack>

				<Divider />

				{/* Broker Information */}
				<Stack spacing="xs">
					<Title size="md" weight="bold">
						Broker Info
					</Title>
					<SimpleGrid cols={2}>
						{Object.entries(worker.broker || {}).map(([key, value]) => (
							<Stack key={key} spacing={0}>
								<Title size="sm" weight="bold">
									{key}
								</Title>
								<Text size="sm" truncate>
									{typeof value === 'object'
										? JSON.stringify(value)
										: String(value)}
								</Text>
							</Stack>
						))}
					</SimpleGrid>
				</Stack>

				<Divider />

				{/* Resource Usage */}
				<Stack spacing="xs">
					<Title size="md" weight="bold">
						Resource Usage
					</Title>
					<SimpleGrid cols={2}>
						{Object.entries(worker.rusage || {}).map(([key, value]) => (
							<Stack key={key} spacing={0}>
								<Title size="sm" weight="bold">
									{key}
								</Title>
								<Text size="sm" truncate>
									{typeof value === 'object'
										? JSON.stringify(value)
										: String(value)}
								</Text>
							</Stack>
						))}
					</SimpleGrid>
				</Stack>

				<Divider />

				{/* Total Statistics */}
				<Stack spacing="xs">
					<Title size="md" weight="bold">
						Total Task Runs
					</Title>
					<SimpleGrid cols={2}>
						{Object.entries(worker.total || {}).map(([key, value]) => (
							<Stack key={key} spacing={0}>
								<Title
									size="sm"
									weight="bold"
									style={{ wordBreak: 'break-word' }}
								>
									{key}
								</Title>
								<Text size="sm">{String(value)}</Text>
							</Stack>
						))}
					</SimpleGrid>
				</Stack>
			</Stack>
		</Drawer>
	);
}
